/**
 * Customer self-serve sign-up (POST /v1/identity/signups). Rendered standalone (AuthLayout), outside the
 * portal shell. The API answers 202 for every email (no account enumeration), so the page always shows the
 * same "check your inbox" message: a new email gets a verification link, a known one a sign-in link.
 * No password and no CAPTCHA; every control is labelled and errors are announced (role="alert").
 * "Continue with Google" (hidden while /google/status says no) needs only the company and the consent: Google supplies and
 * verifies the email, and the account is created when it comes back, so there is no inbox step.
 */
import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, Field, Heading, Panel, Select, Text, TextInput } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bff } from '../../../lib/bff';
import { botCheckHeaders } from '../../../lib/turnstile';
import { urlFieldProps, withHttps } from '../../../lib/url';
import { rememberNext, safeNext } from '../../../lib/deeplink';
import { GoogleButton, useGoogleSignin } from '../../../lib/google-signin';

/** Countries offered on the form (ISO 3166-1 alpha-2); UAE first and default. Names come from Intl. */
export const SIGNUP_COUNTRIES = ['AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'EG', 'JO', 'LB', 'IQ', 'MA', 'TN', 'DZ', 'TR', 'PK', 'IN', 'GB', 'IE', 'DE', 'FR', 'NL', 'US', 'CA', 'AU', 'SG'] as const;
export const DEFAULT_COUNTRY = 'AE';

export function legalLinks(locale: 'en' | 'ar') {
  const base = locale === 'ar' ? 'https://quixomarketing.com/ar' : 'https://quixomarketing.com';
  return { terms: `${base}/terms`, privacy: `${base}/privacy` };
}

function countryName(code: string, locale: string): string {
  try { return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code; } catch { return code; }
}

export function SignUpPage() {
  const { t, locale } = useApp();
  const [params] = useSearchParams();
  const next = params.get('next') ? safeNext(params.get('next')) : null;
  const consentId = useId();
  const [form, setForm] = useState({ name: '', email: '', company: '', website: '', country: DEFAULT_COUNTRY });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const googleOn = useGoogleSignin();
  const formRef = useRef<HTMLFormElement>(null);
  const countries = useMemo(() => SIGNUP_COUNTRIES.map((c) => ({ code: c, name: countryName(c, locale) })), [locale]);
  const links = legalLinks(locale);
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setConsentError(null);
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) { setError(t('shell.signup.required')); return; }
    if (!consent) { setConsentError(t('shell.signup.consentRequired')); return; }
    setBusy(true);
    try {
      await bff('/v1/identity/signups', { method: 'POST', headers: await botCheckHeaders(e.currentTarget as HTMLElement, locale), body: JSON.stringify({
        name: form.name.trim(), email: form.email.trim(), company: form.company.trim(),
        ...(withHttps(form.website) ? { website: withHttps(form.website) } : {}), country: form.country, consent: true, locale,
      }) });
      setSent(form.email.trim());
    } catch (err) {
      setError(err instanceof BffError && err.status === 429 ? t('shell.signup.rateLimited') : err instanceof BffError && err.status === 403 ? t('shell.error.botCheck') : err instanceof BffError && err.status === 400 ? t('shell.signup.invalid') : t('shell.error.action'));
    } finally { setBusy(false); }
  };

  const signUpWithGoogle = async () => {
    setError(null); setConsentError(null);
    if (!form.company.trim()) { setError(t('shell.signup.googleRequired')); return; }
    if (!consent) { setConsentError(t('shell.signup.consentRequired')); return; }
    setBusy(true);
    try {
      const { url } = await bff<{ url: string }>('/v1/identity/google/signup', { method: 'POST', headers: await botCheckHeaders(formRef.current as HTMLElement, locale), body: JSON.stringify({
        company: form.company.trim(), ...(form.name.trim() ? { name: form.name.trim() } : {}),
        ...(withHttps(form.website) ? { website: withHttps(form.website) } : {}), country: form.country, consent: true, locale,
      }) });
      rememberNext(next);
      window.location.assign(url);
    } catch (err) {
      setBusy(false);
      setError(err instanceof BffError && err.status === 429 ? t('shell.signup.rateLimited') : err instanceof BffError && err.status === 403 ? t('shell.error.botCheck') : err instanceof BffError && err.status === 400 ? t('shell.signup.invalid') : t('shell.error.action'));
    }
  };

  const text = (k: keyof typeof form, label: string, extra: { type?: string; autoComplete?: string; required?: boolean; hint?: string; maxLength?: number } = {}) => (
    <Field label={label} hint={extra.hint}>
      {({ id, describedBy }) => (
        <TextInput id={id} type={extra.type ?? 'text'} autoComplete={extra.autoComplete} required={extra.required} maxLength={extra.maxLength ?? 120}
          value={form[k]} aria-describedby={describedBy} onChange={(e) => set(k)(e.target.value)}
          {...(extra.type === 'url' ? urlFieldProps(form[k], set(k)) : {})} />
      )}
    </Field>
  );

  return (
    <section className="qm-page" aria-labelledby="page-title" data-testid="signup-page">
      <Panel>
        <Heading level={1} id="page-title">{t('shell.signup.title')}</Heading>
        {sent ? (
          <Text role="status">{t('shell.signup.sent', { email: sent })}</Text>
        ) : (
          <>
            <Text>{t('shell.signup.subtitle')}</Text>
            <form ref={formRef} onSubmit={submit} noValidate>
              {text('name', t('shell.signup.name'), { autoComplete: 'name', required: true })}
              {text('email', t('shell.signup.email'), { type: 'email', autoComplete: 'email', required: true, maxLength: 254 })}
              {text('company', t('shell.signup.company'), { autoComplete: 'organization', required: true })}
              {text('website', t('shell.signup.website'), { type: 'url', autoComplete: 'url', hint: t('shell.signup.websiteHint'), maxLength: 200 })}
              <Field label={t('shell.signup.country')}>
                {({ id }) => (
                  <Select id={id} autoComplete="country" value={form.country} onChange={(e) => set('country')(e.target.value)}>
                    {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </Select>
                )}
              </Field>
              <div className="qm-auth__consent">
                <input id={consentId} type="checkbox" checked={consent} required aria-invalid={consentError ? true : undefined}
                  aria-describedby={consentError ? `${consentId}-err` : undefined} onChange={(e) => setConsent(e.target.checked)} />
                <label htmlFor={consentId}>
                  {t('shell.signup.consentBefore')}
                  <a href={links.terms} target="_blank" rel="noopener noreferrer">{t('shell.signup.consentTerms')}</a>
                  {t('shell.signup.consentBetween')}
                  <a href={links.privacy} target="_blank" rel="noopener noreferrer">{t('shell.signup.consentPrivacy')}</a>
                  {t('shell.signup.consentAfter')}
                </label>
              </div>
              {consentError ? <p className="qm-field__error" id={`${consentId}-err`} role="alert">{consentError}</p> : null}
              {error ? <p className="qm-field__error" role="alert">{error}</p> : null}
              <Button variant="primary" type="submit" disabled={busy}>{t('shell.signup.submit')}</Button>
              {googleOn ? <>
                <GoogleButton label={t('shell.signin.google')} onClick={() => void signUpWithGoogle()} disabled={busy} testId="google-signup" />
                <Text tone="muted">{t('shell.signup.googleHint')}</Text>
              </> : null}
            </form>
          </>
        )}
        <Text>{t('shell.signup.haveAccount')} <Link to={next ? `/signin?next=${encodeURIComponent(next)}` : '/signin'} data-testid="to-signin">{t('shell.signup.toSignin')}</Link></Text>
      </Panel>
    </section>
  );
}
